/**
 * Screen base. Each §13.1 screen owns a root element, a focus order, and an
 * `onAction` sink. Screens are constructed once at boot and shown/hidden — no
 * screen rebuilds its tree on a transition.
 */

import { div } from '../dom';
import type { ScreenName, UIAction } from '../types';

export type ActionSink = (action: UIAction) => void;

export abstract class Screen {
  readonly root: HTMLDivElement;
  readonly name: ScreenName;
  protected emit: ActionSink;
  protected stops: HTMLElement[] = [];

  constructor(name: ScreenName, emit: ActionSink, extraClass?: string) {
    this.name = name;
    this.emit = emit;
    this.root = div('screen screen-' + name + (extraClass ? ' ' + extraClass : ''));
    this.root.hidden = true;
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-label', name);
  }

  /** Focus order for gamepad/keyboard navigation (§13.1 lists one per screen). */
  focusStops(): readonly HTMLElement[] {
    return this.stops;
  }

  /** Called every time the screen becomes visible; refresh volatile content here. */
  onShow(): void {}

  onHide(): void {}

  /** B / Esc handling. Return true when the screen consumed the input. */
  onBack(): boolean {
    return false;
  }

  show(): void {
    this.root.hidden = false;
    this.onShow();
  }

  hide(): void {
    this.onHide();
    this.root.hidden = true;
  }
}
